import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { store } from "./store/root";
import App from "./App";
import GuestbookApp from "./guestbook/GuestbookApp";
import PhotoGalleryApp from "./photo_gallery/PhotoGalleryApp";
import CampaignApp from "./campaign/CampaignApp";
import BookApp from "./book/BookApp";
import ExitIntent from "./exit_intent/ExitIntent";
import * as Sentry from "@sentry/react";
import ErrorBoundary from "../components/common/ErrorBoundary";
import { ignoredErrors } from "../components/common/sentry-config";

if (import.meta.env.VITE_SENTRY_ENABLED == "true") {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_APP_ENV,
        release: window.sentryRelease,
        ignoreErrors: ignoredErrors,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            Sentry.thirdPartyErrorFilterIntegration({
                behaviour: "drop-error-if-contains-third-party-frames",
                filter: ["tukios-websites-react"],
            }),
        ],

        // Performance Monitoring
        tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0,
        // Session Replay
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 0,
    });

    // Add section tag
    Sentry.setTag("errorSource.name", "widget");
}

const app = document.getElementById("tukios-obituary-app");

ReactDOM.render(
    <ErrorBoundary
        beforeCapture={(scope) => {
            scope.setTag("errorSource.component", "PublicObituaryApp");
        }}
    >
        <Provider store={store}>
            <App data={app} />
        </Provider>
    </ErrorBoundary>,
    app
);

const guestbookApp = document.getElementById("tukios-obituary-guestbook");

if (guestbookApp) {
    const getGuestbookAttributeValue = (attributeName) => {
        return guestbookApp.getAttribute(attributeName);
    };

    let guestbookProps = {
        disableObituaryPosts: guestbookApp.hasAttribute("data-disable-posts") &&
        !/^(false|0|off|no)$/i.test(
            guestbookApp.getAttribute("data-disable-posts")
        ),
        primaryButtonClasses: getGuestbookAttributeValue('data-guestbook-primary-button-classes'),
        secondaryButtonClasses: getGuestbookAttributeValue('data-guestbook-secondary-button-classes'),
        guestbookTextClasses: getGuestbookAttributeValue('data-guestbook-text-classes'),
        guestbookNameClasses: getGuestbookAttributeValue('data-guestbook-name-classes')
    };

    guestbookProps = Object.fromEntries(
        Object.entries(guestbookProps).filter(([_, value]) => value !== null && value !== undefined)
    );

    ReactDOM.render(
        <ErrorBoundary
            beforeCapture={(scope) => {
                scope.setTag("errorSource.component", "GuestbookApp");
            }}
        >
            <Provider store={store}>
                <GuestbookApp {...guestbookProps} />
            </Provider>
        </ErrorBoundary>,
        guestbookApp
    );
}

const gallery = document.getElementById("tukios-obituary-gallery");

if (gallery) {
    const getAttributeValue = (attributeName) => {
        return gallery.getAttribute(attributeName);
    };

    let galleryProps = {
        titleClasses: getAttributeValue('data-gallery-title-classes'),
        showContainer: getAttributeValue('data-gallery-show-container'),
        buttonClasses: getAttributeValue('data-gallery-button-classes'),
        fitContainer: getAttributeValue('data-gallery-fit-container')
    };

    galleryProps = Object.fromEntries(
        Object.entries(galleryProps).filter(([_, value]) => value !== null && value !== undefined)
    );
    ReactDOM.render(
        <ErrorBoundary
            beforeCapture={(scope) => {
                scope.setTag("errorSource.component", "PhotoGalleryApp");
            }}
        >
            <Provider store={store}>
                <PhotoGalleryApp {...galleryProps} />
            </Provider>
        </ErrorBoundary>,
        gallery
    );
}

const campaignApp = document.getElementById("tukios-obituary-campaigns");

if (campaignApp) {
    ReactDOM.render(
        <>
            <ErrorBoundary
                beforeCapture={(scope) => {
                    scope.setTag("errorSource.component", "CampaignApp");
                }}
            >
                <Provider store={store}>
                    <CampaignApp />
                </Provider>
            </ErrorBoundary>
        </>,
        campaignApp
    );
}

const bookApp = document.getElementById("tukios-obituary-book");

if (bookApp) {
    const getAttributeValue = (attrName) => {
        return bookApp.getAttribute(attrName);
    };

    let bookWidgetProps = {
        titleText: getAttributeValue('data-title-text'),
        titleClassName: getAttributeValue('data-title-class'),
        nameClassName: getAttributeValue('data-name-class'),
        descriptionClassName: getAttributeValue('data-description-class'),
        shareMemoryTitleClassName: getAttributeValue('data-share-memory-title-class'),
        shareMemoryDescriptionClassName: getAttributeValue('data-share-memory-description-class'),
        viewBookButtonProps: getAttributeValue('data-view-book-button-props'),
        buyBookButtonProps: getAttributeValue('data-buy-book-button-props'),
        shareMemoryButtonProps: getAttributeValue('data-share-memory-button-props'),
        containerClassName: getAttributeValue('data-container-class'),
        leftSectionClassName: getAttributeValue('data-left-section-class'),
        rightSectionClassName: getAttributeValue('data-right-section-class')
    };

    bookWidgetProps = Object.fromEntries(
        Object.entries(bookWidgetProps).filter(([_, value]) => value !== null && value !== undefined)
    );

    ReactDOM.render(
        <>
            <ErrorBoundary
                beforeCapture={(scope) => {
                    scope.setTag("errorSource.component", "ObituaryBookApp");
                }}
            >
                <Provider store={store}>
                    <BookApp {...bookWidgetProps} />
                </Provider>
            </ErrorBoundary>
        </>,
        bookApp
    );
}

const exitIntent = document.getElementById("tukios-obituary-exit-intent-modal");

if (exitIntent) {
    ReactDOM.render(
        <ErrorBoundary
            beforeCapture={(scope) => {
                scope.setTag("errorSource.component", "ExitIntent");
            }}
        >
            <Provider store={store}>
                <ExitIntent />
            </Provider>
        </ErrorBoundary>,
        exitIntent
    );
}
